import type { DateBasedTriggerMetadata } from '@reach/shared-types/business-automation';
import {
  AutomationTemplateSegmentTypeEnum,
  type AutomationTemplateContents,
} from '@reach/shared-types/automation-template';
import { Calendar, Undo2 } from 'lucide-react';
import React, { useContext, useState } from 'react';
import { AutomationEditorContext } from '../AutomationEditorContext';
import { useSegmentDefinitions } from '../useSegmentDefinitions';
import type { SegmentDefinitionSummary } from '../segment-definitions-api';
import {
  DEFAULT_AUDIENCE_LABEL,
  formatSendsToClause,
  LOADING_AUDIENCE_LABEL,
  MISSING_AUDIENCE_LABEL,
} from './date-trigger-step-copy';
import styles from '../EditAutomationTemplate.module.css';
import { DateTriggerConfigDialog } from './DateTriggerConfigDialog';

const { triggerStep, iconBadge, triggerUnsavedBadge, triggerUndoButton } = styles;

function getDateDescription(triggerMetadata: DateBasedTriggerMetadata): string {
  const { monthToTrigger, dayToTrigger } = triggerMetadata;
  if (monthToTrigger && dayToTrigger) {
    const date = new Date(2024, monthToTrigger - 1, dayToTrigger);
    const monthName = date.toLocaleDateString('en-US', { month: 'long' });
    return `Every year on ${monthName} ${dayToTrigger}`;
  }
  return 'Select a date';
}

function getAudienceClause(
  includeSegmentDefaults: AutomationTemplateContents['includeSegmentDefaults'] | undefined,
  segmentDefinitions: SegmentDefinitionSummary[] | undefined,
): string {
  const firstDefault = includeSegmentDefaults?.[0];
  const isDefaultAllUsers =
    !firstDefault || firstDefault === AutomationTemplateSegmentTypeEnum.ALL_USERS;
  if (isDefaultAllUsers) {
    return formatSendsToClause(DEFAULT_AUDIENCE_LABEL);
  }
  const canonicalVersionId = typeof firstDefault === 'string' ? firstDefault : firstDefault.id;
  // Segments still loading — can't yet tell whether the referenced segment exists.
  if (!segmentDefinitions) {
    return LOADING_AUDIENCE_LABEL;
  }
  const matchingSegment = segmentDefinitions.find(
    segment => segment.canonicalVersionId === canonicalVersionId,
  );
  return matchingSegment ? formatSendsToClause(matchingSegment.name) : MISSING_AUDIENCE_LABEL;
}

interface DateTriggerStepProps {
  triggerMetadata: DateBasedTriggerMetadata;
}

export const DateTriggerStep: React.FC<DateTriggerStepProps> = ({ triggerMetadata }) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { state, undoTrigger } = editor;
  const { data: segmentDefinitions } = useSegmentDefinitions();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const isDirty = state.dirtySteps.trigger;
  const dateDescription = getDateDescription(triggerMetadata);
  const audienceClause = getAudienceClause(
    state.localContents.includeSegmentDefaults,
    segmentDefinitions,
  );
  const description = `${dateDescription} · ${audienceClause}`;

  return (
    <article className={triggerStep}>
      <header>
        <span className={iconBadge}>
          <Calendar />
        </span>
        <h3>
          Trigger
          {isDirty && (
            <>
              <span className={triggerUnsavedBadge}>Unsaved changes</span>
              <button
                type="button"
                onClick={undoTrigger}
                className={triggerUndoButton}
                aria-label="Undo changes"
                title="Undo changes">
                <Undo2 />
              </button>
            </>
          )}
        </h3>
      </header>
      <p>{description}</p>
      <button onClick={() => setIsDialogOpen(true)} type="button">
        Configure →
      </button>
      {isDialogOpen && (
        <DateTriggerConfigDialog
          onClose={() => setIsDialogOpen(false)}
          triggerMetadata={triggerMetadata}
        />
      )}
    </article>
  );
};
